import { useState, useEffect, useCallback, useRef } from 'react';
import { testStudentApi } from '../api';

// ── inline styles ─────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500,
    background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: '#1e2230', color: '#fff',
    position: 'sticky', top: 0, zIndex: 10, flexWrap: 'wrap', gap: 10,
  },
  testName: { fontWeight: 700, fontSize: 16 },
  timerBase: { fontWeight: 800, fontSize: 22, fontVariantNumeric: 'tabular-nums', padding: '4px 14px', borderRadius: 8 },
  answeredCount: { fontSize: 13, color: '#a0aec0' },
  body: { display: 'flex', flex: 1, minHeight: 0 },
  navigator: {
    width: 140, minWidth: 120, background: '#f7fafc', borderRight: '1px solid #e2e8f0',
    padding: '16px 12px', overflowY: 'auto', flexShrink: 0,
  },
  navigatorTitle: { fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 },
  navGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  navSquare: (state) => ({
    width: 36, height: 36, borderRadius: 6, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    border: state === 'current' ? '2px solid #0066CC'
           : state === 'answered' ? '2px solid #22863a'
           : '2px solid #cbd5e0',
    background: state === 'answered' ? '#22863a'
               : state === 'current' ? '#ebf8ff'
               : '#fff',
    color: state === 'answered' ? '#fff'
          : state === 'current' ? '#0066CC'
          : '#4a5568',
    transition: 'all .15s',
  }),
  questionArea: { flex: 1, padding: '24px', overflowY: 'auto', minWidth: 0 },
  questionMeta: { fontSize: 12, color: '#718096', marginBottom: 8, fontWeight: 600 },
  questionText: { fontSize: 16, fontWeight: 600, color: '#1a202c', lineHeight: 1.6, marginBottom: 24 },
  optionBtn: (selected) => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px',
    borderRadius: 8, border: selected ? '2px solid #0066CC' : '1.5px solid #e2e8f0',
    background: selected ? '#0066CC' : '#fff',
    color: selected ? '#fff' : '#2d3748',
    fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 10,
    transition: 'all .15s',
  }),
  optionLabel: { fontWeight: 700, marginRight: 8 },
  controls: {
    display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20,
    borderTop: '1px solid #f0f4f8', marginTop: 16, flexWrap: 'wrap',
  },
  submitBtn: {
    marginLeft: 'auto', background: '#c05621', color: '#fff',
    border: 'none', borderRadius: 8, padding: '9px 20px',
    fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
};

function formatTime(secs) {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function timerStyle(secs) {
  if (secs > 300) return { ...styles.timerBase, background: '#f0fff4', color: '#22863a' };
  if (secs > 120) return { ...styles.timerBase, background: '#fffaf0', color: '#c05621' };
  return { ...styles.timerBase, background: '#fff5f5', color: '#c53030', animation: 'pulse 1s infinite' };
}

export default function TakeTest({ attemptId, onFinish }) {
  const [questions, setQuestions] = useState([]);
  const [attemptInfo, setAttemptInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const timerRef = useRef(null);
  const submittedRef = useRef(false);

  const handleSubmit = useCallback(async (force = false, currentAnswers) => {
    if (submittedRef.current) return;
    const ans = currentAnswers || answers;
    const answeredCount = Object.keys(ans).filter(k => ans[k]).length;
    if (!force && !window.confirm(
      `Submit test? You have answered ${answeredCount} of ${questions.length} questions.`
    )) return;
    submittedRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const payload = questions.map(q => ({
        questionId: q.id,
        selectedOption: ans[q.id] || null,
      }));
      const result = await testStudentApi.submitTest(attemptId, payload);
      onFinish(result);
    } catch (e) {
      setErr(e.message);
      submittedRef.current = false;
      setSubmitting(false);
    }
  }, [answers, questions, attemptId, onFinish]);

  useEffect(() => {
    testStudentApi.getAttemptQuestions(attemptId)
      .then(data => {
        if (data.timedOut) {
          onFinish({
            status: 'TIMED_OUT', score: 0, totalMarks: 0,
            correctCount: 0, wrongCount: 0,
            skippedCount: data.attempt?.numQuestions || 0,
            percentage: 0, grade: 'F', questions: [],
          });
          return;
        }
        if (data.alreadySubmitted) {
          // Attempt was already submitted — go back so the student sees their real result in "My Results"
          onFinish(null);
          return;
        }
        setAttemptInfo(data.attempt);
        setQuestions(data.questions || []);
        setTimeRemaining(data.timeRemainingSeconds || 0);
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [attemptId, onFinish]);

  // Timer
  useEffect(() => {
    if (loading || questions.length === 0 || timeRemaining <= 0) return;
    let remaining = timeRemaining;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        // auto-submit with latest answers captured via ref
        setAnswers(current => {
          handleSubmit(true, current);
          return current;
        });
      }
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, questions.length]);

  const selectOption = (option) => {
    const q = questions[currentIdx];
    if (!q) return;
    setAnswers(a => {
      const updated = { ...a, [q.id]: option };
      // Auto-advance to next unanswered question
      const nextUnanswered = questions.findIndex((qq, idx) => idx > currentIdx && !updated[qq.id]);
      if (nextUnanswered !== -1) setCurrentIdx(nextUnanswered);
      else if (currentIdx < questions.length - 1) setCurrentIdx(i => i + 1);
      return updated;
    });
  };

  const answeredCount = Object.keys(answers).filter(k => answers[k]).length;
  const currentQ = questions[currentIdx];

  if (loading) return <div className="loading-msg">Loading test…</div>;
  if (err) return <div className="error-msg">{err} <button className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={() => setErr('')}>Retry</button></div>;

  const optionEntries = currentQ ? [
    ['A', currentQ.optionA],
    ['B', currentQ.optionB],
    ['C', currentQ.optionC],
    ['D', currentQ.optionD],
  ] : [];

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .55; }
        }
        @media (max-width: 600px) {
          .take-test-nav { width: 90px !important; min-width: 80px !important; }
          .take-test-nav-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .take-test-nav-sq { width: 30px !important; height: 30px !important; font-size: 11px !important; }
        }
      `}</style>
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.testName}>
            {attemptInfo?.testName || 'Test'}
          </div>
          <div style={timerStyle(timeRemaining)}>
            <i className="fas fa-clock" style={{ marginRight: 6, fontSize: 14 }}></i>
            {formatTime(timeRemaining)}
          </div>
          <div style={styles.answeredCount}>
            {answeredCount}/{questions.length} answered
          </div>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Question Navigator */}
          <div style={styles.navigator} className="take-test-nav">
            <div style={styles.navigatorTitle}>Questions</div>
            <div style={styles.navGrid} className="take-test-nav-grid">
              {questions.map((q, i) => {
                const state = i === currentIdx ? 'current' : answers[q.id] ? 'answered' : 'unanswered';
                return (
                  <div
                    key={q.id}
                    style={styles.navSquare(state)}
                    className="take-test-nav-sq"
                    onClick={() => setCurrentIdx(i)}
                    title={`Question ${i + 1}`}
                  >
                    {i + 1}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Question Area */}
          <div style={styles.questionArea}>
            {currentQ ? (
              <>
                <div style={styles.questionMeta}>Question {currentIdx + 1} of {questions.length}</div>
                <div style={styles.questionText}>{currentQ.questionText}</div>

                {optionEntries.map(([letter, text]) => text != null && (
                  <button
                    key={letter}
                    style={styles.optionBtn(answers[currentQ.id] === letter)}
                    onClick={() => selectOption(letter)}
                  >
                    <span style={styles.optionLabel}>{letter}.</span>
                    {text}
                  </button>
                ))}

                <div style={styles.controls}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                    disabled={currentIdx === 0}
                  >
                    <i className="fas fa-arrow-left"></i> Prev
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setCurrentIdx(i => Math.min(questions.length - 1, i + 1))}
                    disabled={currentIdx === questions.length - 1}
                  >
                    Next <i className="fas fa-arrow-right"></i>
                  </button>
                  <button
                    style={styles.submitBtn}
                    onClick={() => handleSubmit(false)}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting…' : <><i className="fas fa-paper-plane"></i> Submit Test</>}
                  </button>
                </div>

                {err && <div className="error-msg" style={{ marginTop: 12 }}>{err}</div>}
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#888', paddingTop: 40 }}>No questions in this test.</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
